import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Big from 'big.js';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parseISO, subDays } from 'date-fns';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import AnimatedPage from '@/components/AnimatedPage';
import CurrencyAmounts from '@/components/CurrencyAmounts';
import { dictionariesService, merchantsService, payoutsService, tradersService, transactionsService } from '@/services';
import type {
  TransactionsStatisticDailyResponse,
  TransactionsStatisticSummaryResponse,
} from '@/services/transactions/transactions.service';
import type { PaymentSystem } from '@/types/common/payment';
import type { Currency } from '@/types/common/currency';
import type { PayoutStatisticDaily, PayoutStatisticSummary } from '@/types/payout';
import type { TraderLookup, TraderRate } from '@/types/trader';
import type { Merchant, MerchantFakeCheckStatistics } from '@/types/merchant';
import { formatAmount } from '@/utils/amount';

type StatisticType = 'transactions' | 'payouts' | 'merchants';
type Summary = TransactionsStatisticSummaryResponse | PayoutStatisticSummary;
type Daily = TransactionsStatisticDailyResponse | PayoutStatisticDaily;

interface StatisticDashboardProps {
  type: StatisticType;
}

const todayString = () => format(new Date(), 'yyyy-MM-dd');
const weekAgoString = () => format(subDays(new Date(), 6), 'yyyy-MM-dd');

const getDateValue = (value: string): Date | null => (value ? parseISO(value) : null);
const getDateString = (value: Date | null): string => (value ? format(value, 'yyyy-MM-dd') : '');

const getCurrencyCode = (currency: PaymentSystem['currency']): string => {
  if (!currency) return '';
  return typeof currency === 'string' ? currency : currency.code || currency.iso_code || '';
};

const getTraderLabel = (trader: TraderLookup): string =>
  trader.name || trader.username || trader.telegram_nickname || trader.id;

const getErrorMessage = (err: any): string => {
  const detail = err?.response?.data?.detail || err?.message;
  if (typeof detail === 'string') {
    if (detail.includes('from_date and to_date')) {
      return 'Укажите обе даты периода или очистите обе даты.';
    }
    if (detail.includes('from_date must be less')) {
      return 'Дата начала должна быть меньше или равна дате окончания.';
    }
    if (detail.includes('min_amount must be less')) {
      return 'Минимальная сумма должна быть меньше или равна максимальной.';
    }
    return detail;
  }
  return 'Ошибка при загрузке статистики';
};

const summaryConfig = {
  transactions: {
    title: 'Статистика транзакций',
    totalLabel: 'Всего транзакций',
    totalKey: 'total_transactions',
    successLabel: 'Успешные',
    successKey: 'successful_transactions',
    failedLabel: 'Отклонённые',
    failedKey: 'failed_transactions',
    earnedLabel: 'Комиссия трейдеров',
    averageLabel: 'Средний чек',
  },
  payouts: {
    title: 'Статистика выплат',
    totalLabel: 'Всего заявок',
    totalKey: 'total_payouts',
    successLabel: 'Успешные (SUCCESS)',
    successKey: 'successful_payouts',
    failedLabel: 'Отклонённые (DECLINED)',
    failedKey: 'failed_payouts',
    earnedLabel: 'Комиссия трейдеров',
    averageLabel: 'Средняя сумма выплаты',
  },
  merchants: {
    title: 'Статистика мерчантов',
    totalLabel: 'Всего транзакций',
    totalKey: 'total_transactions',
    successLabel: 'Успешные',
    successKey: 'successful_transactions',
    failedLabel: 'Отклонённые',
    failedKey: 'failed_transactions',
    earnedLabel: 'Комиссия трейдеров',
    averageLabel: 'Средний чек',
  },
} as const;

const countsConfig = {
  transactions: {
    key: 'daily_transactions',
    total: 'transaction_count',
    success: 'successful_transaction_count',
    failed: 'failed_transaction_count',
    totalLabel: 'Всего',
    successLabel: 'Успешные',
    failedLabel: 'Отклонённые',
  },
  payouts: {
    key: 'daily_payouts',
    total: 'payout_count',
    success: 'successful_payout_count',
    failed: 'failed_payout_count',
    totalLabel: 'Всего',
    successLabel: 'Успешные',
    failedLabel: 'Отклонённые',
  },
  merchants: {
    key: 'daily_transactions',
    total: 'transaction_count',
    success: 'successful_transaction_count',
    failed: 'failed_transaction_count',
    totalLabel: 'Всего',
    successLabel: 'Успешные',
    failedLabel: 'Отклонённые',
  },
} as const;

const StatisticDashboard: React.FC<StatisticDashboardProps> = ({ type }) => {
  const config = summaryConfig[type];
  const countConfig = countsConfig[type];

  const [traders, setTraders] = useState<TraderLookup[]>([]);
  const [selectedTrader, setSelectedTrader] = useState<TraderLookup | null>(null);
  const [traderSearch, setTraderSearch] = useState('');
  const [traderSearchLoading, setTraderSearchLoading] = useState(false);
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [paymentSystems, setPaymentSystems] = useState<PaymentSystem[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [traderRates, setTraderRates] = useState<TraderRate[]>([]);

  const [fromDate, setFromDate] = useState(weekAgoString());
  const [toDate, setToDate] = useState(todayString());
  const [traderId, setTraderId] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [currency, setCurrency] = useState('');
  const [paymentSystemId, setPaymentSystemId] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');

  const [summary, setSummary] = useState<Summary | null>(null);
  const [daily, setDaily] = useState<Daily | null>(null);
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fakeCheckStatistics, setFakeCheckStatistics] = useState<MerchantFakeCheckStatistics[]>([]);

  const hasOneDateOnly = Boolean(fromDate) !== Boolean(toDate);
  const hasInvalidRange = Boolean(fromDate && toDate && fromDate > toDate);
  const shouldRequirePeriod = type === 'payouts';
  const amountErrorText = useMemo(() => {
    try {
      const min = minAmount.trim() ? new Big(minAmount) : null;
      const max = maxAmount.trim() ? new Big(maxAmount) : null;
      if (min?.lt(0) || max?.lt(0)) return 'Сумма не может быть отрицательной.';
      if (min && max && min.gt(max)) {
        return 'Минимальная сумма должна быть меньше или равна максимальной.';
      }
      return '';
    } catch {
      return 'Введите корректную сумму.';
    }
  }, [maxAmount, minAmount]);
  const isApplyDisabled =
    hasOneDateOnly ||
    hasInvalidRange ||
    Boolean(amountErrorText) ||
    (shouldRequirePeriod && !fromDate && !toDate);

  const dateErrorText = useMemo(() => {
    if (hasOneDateOnly) return 'Укажите обе даты периода или очистите обе даты.';
    if (hasInvalidRange) return 'Дата начала должна быть меньше или равна дате окончания.';
    if (shouldRequirePeriod && !fromDate && !toDate) return 'Для выплат нужен период.';
    return '';
  }, [fromDate, hasInvalidRange, hasOneDateOnly, shouldRequirePeriod, toDate]);

  const filtersParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (fromDate && toDate) {
      params.from_date = fromDate;
      params.to_date = toDate;
    }
    if (traderId) params.trader_id = traderId;
    if (merchantId) params.merchant_id = merchantId;
    if (currency) params.currency = currency;
    if (paymentSystemId) params.payment_system_id = paymentSystemId;
    if (minAmount.trim()) params.min_amount = minAmount.trim();
    if (maxAmount.trim()) params.max_amount = maxAmount.trim();
    if (type === 'transactions' || type === 'merchants') params.direction = 'IN';
    if (type === 'merchants') params.entity = 'merchant';
    return params;
  }, [currency, fromDate, maxAmount, merchantId, minAmount, paymentSystemId, toDate, traderId, type]);

  const dailyParams = useMemo(() => {
    const params = { ...filtersParams };
    params.from_date = fromDate || weekAgoString();
    params.to_date = toDate || todayString();
    return params as Record<string, string> & { from_date: string; to_date: string };
  }, [filtersParams, fromDate, toDate]);

  const filteredPaymentSystems = useMemo(() => {
    const statisticDirection = type === 'payouts' ? 'OUT' : 'IN';
    const matchingRates = traderRates.filter((rate) => {
      if (rate.direction !== statisticDirection) return false;
      if (traderId && rate.trader_id !== traderId) return false;
      return Boolean(rate.payment_system_id);
    });
    const allowedIds = new Set(matchingRates.map((rate) => rate.payment_system_id));

    return paymentSystems.filter((ps) => {
      if (currency && getCurrencyCode(ps.currency) !== currency) return false;
      return allowedIds.size === 0 ? false : allowedIds.has(ps.id);
    });
  }, [currency, paymentSystems, traderId, traderRates, type]);

  const loadFilters = useCallback(async () => {
    try {
      setFiltersLoading(true);
      const [merchantsData, paymentSystemsData, currenciesData, traderRatesData] = await Promise.all([
        type === 'merchants' ? merchantsService.getMerchants() : Promise.resolve([] as Merchant[]),
        type === 'merchants' ? Promise.resolve([] as PaymentSystem[]) : dictionariesService.getPaymentSystems(),
        dictionariesService.getCurrencies(),
        type === 'merchants' ? Promise.resolve([] as TraderRate[]) : tradersService.getTraderRates(),
      ]);
      setMerchants(merchantsData || []);
      setPaymentSystems(paymentSystemsData || []);
      setCurrencies(currenciesData || []);
      setTraderRates(traderRatesData || []);
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setFiltersLoading(false);
    }
  }, [type]);

  const loadStatistics = useCallback(async () => {
    if (isApplyDisabled) return;
    try {
      setLoading(true);
      setError(null);

      if (type === 'transactions' || type === 'merchants') {
        const fakeCheckParams = {
          from_date: dailyParams.from_date,
          to_date: dailyParams.to_date,
          ...(merchantId ? { merchant_id: merchantId } : {}),
        };
        const [summaryData, dailyData, fakeCheckData] = await Promise.all([
          transactionsService.getStatisticSummary(filtersParams),
          transactionsService.getStatisticDaily(dailyParams),
          type === 'merchants'
            ? merchantsService.getFakeCheckStatistics(fakeCheckParams)
            : Promise.resolve([] as MerchantFakeCheckStatistics[]),
        ]);
        if (Array.isArray(summaryData)) {
          setSummary(null);
          setError('Эндпоинт статистики вернул список вместо агрегированной сводки.');
        } else {
          setSummary(summaryData);
        }
        setDaily(dailyData);
        setFakeCheckStatistics(fakeCheckData);
      } else {
        const [summaryData, dailyData] = await Promise.all([
          payoutsService.getStatisticSummary(filtersParams),
          payoutsService.getStatisticDaily(dailyParams),
        ]);
        setSummary(summaryData);
        setDaily(dailyData);
        setFakeCheckStatistics([]);
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
      setSummary(null);
      setDaily(null);
    } finally {
      setLoading(false);
    }
  }, [dailyParams, filtersParams, isApplyDisabled, merchantId, type]);

  useEffect(() => {
    void loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    if (type === 'merchants') return undefined;

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setTraderSearchLoading(true);
      try {
        const results = await tradersService.searchTraders(traderSearch.trim());
        if (!cancelled) setTraders(results);
      } catch (err: any) {
        if (!cancelled) setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setTraderSearchLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [traderSearch, type]);

  useEffect(() => {
    if (!filtersLoading) {
      void loadStatistics();
    }
  }, [filtersLoading, loadStatistics]);

  useEffect(() => {
    if (paymentSystemId && !filteredPaymentSystems.some((ps) => ps.id === paymentSystemId)) {
      setPaymentSystemId('');
    }
  }, [filteredPaymentSystems, paymentSystemId]);

  const resetFilters = () => {
    setFromDate(type === 'transactions' ? '' : weekAgoString());
    setToDate(type === 'transactions' ? '' : todayString());
    setTraderId('');
    setSelectedTrader(null);
    setTraderSearch('');
    setMerchantId('');
    setCurrency('');
    setPaymentSystemId('');
    setMinAmount('');
    setMaxAmount('');
    setError(null);
  };

  const earnedData = daily?.daily_earned.map((item) => ({
    date: item.date,
    earned: item.earned_usdt != null ? Number(item.earned_usdt) : null,
    turnover: item.turnover_usdt != null ? Number(item.turnover_usdt) : null,
  })) || [];

  const counts = (daily?.[countConfig.key as keyof Daily] || []) as Array<Record<string, any>>;
  const countsData = counts.map((item) => ({
    date: item.date,
    total: Number(item[countConfig.total] || 0),
    success: Number(item[countConfig.success] || 0),
    failed: Number(item[countConfig.failed] || 0),
    pending: Number(item[type === 'payouts' ? 'pending_payout_count' : 'pending_transaction_count'] || 0),
    notFound: Number(item.not_found_transaction_count || 0),
    appeal: Number(item.appeal_transaction_count || 0),
  }));

  const totalValue = summary ? summary[config.totalKey as keyof Summary] : 0;
  const successValue = summary ? summary[config.successKey as keyof Summary] : 0;
  const failedValue = summary ? summary[config.failedKey as keyof Summary] : 0;
  const summaryCardWidth = 3;
  const merchantAmounts = summary?.amounts_by_currency;
  const renderMerchantAmount = (field: 'turnover' | 'average_check' | 'total_earned') =>
    <CurrencyAmounts amounts={merchantAmounts} field={field} currency={currency} />;
  const pendingValue = summary ? ('pending_payouts' in summary ? summary.pending_payouts : summary.pending_transactions) : 0;
  const notFoundValue = summary && 'not_found_transactions' in summary ? summary.not_found_transactions : 0;
  const appealValue = summary && 'appeal_transactions' in summary ? summary.appeal_transactions : 0;
  const sr = Number(totalValue) ? Number(successValue) / Number(totalValue) * 100 : 0;

  return (
    <AnimatedPage>
      <Box>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
          {config.title}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {type === 'payouts' ? 'Выплаты OUT: заявки выплат.' : 'Пополнения PayIn (IN): транзакции.'}
          {' '}Период — по дате создания, UTC. SR = SUCCESS / всего. Активные трейдеры — с успешными операциями в периоде.
        </Typography>

        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="Дата от"
                value={getDateValue(fromDate)}
                onChange={(value) => setFromDate(getDateString(value))}
                format="dd.MM.yyyy"
                maxDate={getDateValue(toDate) || new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: Boolean(dateErrorText),
                    size: 'small',
                  } as any,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="Дата до"
                value={getDateValue(toDate)}
                onChange={(value) => setToDate(getDateString(value))}
                format="dd.MM.yyyy"
                minDate={getDateValue(fromDate) || undefined}
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: Boolean(dateErrorText),
                    helperText: dateErrorText,
                    size: 'small',
                  } as any,
                }}
              />
            </Grid>
            {type !== 'merchants' && <Grid item xs={12} sm={6} md={2}>
              <Autocomplete
                options={traders}
                value={selectedTrader}
                inputValue={traderSearch}
                loading={traderSearchLoading}
                filterOptions={(options) => options}
                getOptionLabel={getTraderLabel}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onInputChange={(_event, value, reason) => {
                  setTraderSearch(value);
                  if (
                    reason === 'input'
                    && selectedTrader
                    && value !== getTraderLabel(selectedTrader)
                  ) {
                    setSelectedTrader(null);
                    setTraderId('');
                  }
                }}
                onChange={(_event, value) => {
                  setSelectedTrader(value);
                  setTraderId(value?.id || '');
                }}
                noOptionsText={traderSearch.trim() ? 'Трейдеры не найдены' : 'Начните вводить трейдера'}
                renderInput={(params) => (
                  <TextField {...params} label="Трейдер" size="small" />
                )}
              />
            </Grid>}
            {type === 'merchants' && <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Мерчант</InputLabel>
                <Select
                  value={merchantId}
                  label="Мерчант"
                  onChange={(event: SelectChangeEvent<string>) => setMerchantId(event.target.value)}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                >
                  <MenuItem value="">Все</MenuItem>
                  {merchants.map((merchant) => (
                    <MenuItem key={merchant.id} value={merchant.id}>{merchant.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Валюта</InputLabel>
                <Select
                  value={currency}
                  label="Валюта"
                  onChange={(event: SelectChangeEvent<string>) => setCurrency(event.target.value)}
                >
                  <MenuItem value="">Все</MenuItem>
                  {currencies.map((item) => (
                    <MenuItem key={item.id} value={item.code || item.iso_code}>
                      {item.code || item.iso_code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {type !== 'merchants' && <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Платёжная система</InputLabel>
                <Select
                  value={paymentSystemId}
                  label="Платёжная система"
                  onChange={(event: SelectChangeEvent<string>) =>
                    setPaymentSystemId(event.target.value)
                  }
                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                >
                  <MenuItem value="">Все</MenuItem>
                  {filteredPaymentSystems.map((ps) => (
                    <MenuItem key={ps.id} value={ps.id}>
                      {ps.token}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>}
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Сумма от"
                value={minAmount}
                onChange={(event) => setMinAmount(event.target.value)}
                error={Boolean(amountErrorText)}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Сумма до"
                value={maxAmount}
                onChange={(event) => setMaxAmount(event.target.value)}
                error={Boolean(amountErrorText)}
                helperText={amountErrorText}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Box display="flex" gap={1}>
                <Button variant="outlined" onClick={resetFilters} fullWidth>
                  Сбросить
                </Button>
                <Button
                  variant="contained"
                  onClick={() => void loadStatistics()}
                  disabled={isApplyDisabled}
                  fullWidth
                >
                  Применить
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {filtersLoading || loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Пересчёт в USDT по текущим курсам системы. Оборот и средний чек — по успешным транзакциям.
                {earnedData.some((item) => item.turnover === null)
                  && ' Для части дней нет курса: эти значения не отображаются на графике.'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={summaryCardWidth}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography color="text.secondary" variant="body2">
                      {'\u041e\u0431\u043e\u0440\u043e\u0442 SUCCESS'}
                    </Typography>
                    {renderMerchantAmount('turnover')}
                  </Paper>
                </Grid>
                {<Grid item xs={12} sm={6} md={summaryCardWidth}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography color="text.secondary" variant="body2">{config.earnedLabel}</Typography>
                    {renderMerchantAmount('total_earned')}
                  </Paper>
                </Grid>}
                <Grid item xs={12} sm={6} md={summaryCardWidth}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography color="text.secondary" variant="body2">
                      {config.totalLabel}
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 1 }}>
                      {String(totalValue || 0)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={summaryCardWidth}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography color="text.secondary" variant="body2">
                      {config.averageLabel}
                    </Typography>
                    {renderMerchantAmount('average_check')}
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={summaryCardWidth}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography color="text.secondary" variant="body2">
                      {config.successLabel}
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 1 }}>
                      {String(successValue || 0)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={summaryCardWidth}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography color="text.secondary" variant="body2">
                      {config.failedLabel}
                    </Typography>
                    <Typography variant="h5" sx={{ mt: 1 }}>
                      {String(failedValue || 0)}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={summaryCardWidth}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography color="text.secondary">SR</Typography><Typography variant="h5">{sr.toFixed(2)}%</Typography>
                    <Typography>Активных трейдеров: {summary?.active_traders ?? 0}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={summaryCardWidth}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <Typography>В работе / ожидании{type === 'payouts' ? ' (включая пул AVAILABLE)' : ''}: {pendingValue}</Typography>
                    {type !== 'payouts' && <><Typography>Невыдача (NOT_FOUND): {notFoundValue}</Typography><Typography>Апелляции (APPEAL): {appealValue}</Typography></>}
                    <Typography variant="caption" color="text.secondary">Всего = успешные + отклонённые + в работе{type === 'payouts' ? '' : ' + невыдача + апелляции'}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2, height: 360 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Оборот и комиссия, USDT
                </Typography>
                <ResponsiveContainer width="100%" height="85%">
                  <LineChart data={earnedData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={ (value: number, name: string) => [`${formatAmount(value, { decimals: 2, trimTrailingZeros: false })} USDT`, name]
                      } />
                    <Legend />
                    <Line type="monotone" dataKey="turnover" name="Оборот" stroke="#1976d2" />
                    {<Line type="monotone" dataKey="earned" name="Комиссия" stroke="#2e7d32" />}
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2, height: 360 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Количество
                </Typography>
                <ResponsiveContainer width="100%" height="85%">
                  <BarChart data={countsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="total" name={countConfig.totalLabel} fill="#1976d2" />
                    <Bar dataKey="success" name={countConfig.successLabel} fill="#2e7d32" />
                    <Bar dataKey="failed" name={countConfig.failedLabel} fill="#d32f2f" />
                    <Bar dataKey="pending" name="В работе / ожидании" fill="#ed9c28" />
                    {type !== 'payouts' && <Bar dataKey="notFound" name="Невыдача" fill="#8e5ec4" />}
                    {type !== 'payouts' && <Bar dataKey="appeal" name="Апелляции" fill="#37b3b0" />}
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            {type === 'merchants' && (
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Фейк-чеки по мерчантам
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Активные вердикты FAKE CHECK за выбранный период
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Мерчант</TableCell>
                          <TableCell align="right">Фейк-чеков</TableCell>
                          <TableCell align="right">Сумма фейк-чеков</TableCell>
                          <TableCell align="right">Уникальных клиентов</TableCell>
                          <TableCell>Последний фейк</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fakeCheckStatistics.map((item) => (
                          <TableRow key={item.merchant_id}>
                            <TableCell>{item.merchant_name}</TableCell>
                            <TableCell align="right">{item.fake_check_count}</TableCell>
                            <TableCell align="right">
                              <CurrencyAmounts amounts={item.amounts_by_currency?.filter((part) => part.amount !== null && part.currency).map((part) => ({ currency: part.currency!, turnover: part.amount!, average_check: 0, successful_transactions: part.count, usdt_rate: part.usdt_rate }))} />
                              {item.amounts_by_currency?.some((part) => part.amount === null || !part.currency) && <Typography color="text.secondary" variant="caption">Сумма частичная: есть чеки без связанной транзакции или валюты</Typography>}
                            </TableCell>
                            <TableCell align="right">{item.unique_client_count}</TableCell>
                            <TableCell>
                              {item.last_fake_check_at
                                ? format(parseISO(item.last_fake_check_at), 'dd.MM.yyyy HH:mm')
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                        {fakeCheckStatistics.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              Нет данных за выбранный период
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </AnimatedPage>
  );
};

export default StatisticDashboard;
